<template>
  <div>
    <!-- Crecimiento / Adquisición — independent date-range block -->
    <div class="mb-8">
      <h2 class="text-lg font-semibold text-gray-700 mb-3">Crecimiento / Adquisición</h2>

      <div class="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div class="flex flex-col">
          <label class="text-xs text-content-400 mb-1">Desde</label>
          <input type="date" v-model="acquisitionFrom" class="border px-3 py-2 rounded" />
        </div>
        <div class="flex flex-col">
          <label class="text-xs text-content-400 mb-1">Hasta</label>
          <input type="date" v-model="acquisitionTo" class="border px-3 py-2 rounded" />
        </div>
        <button @click="loadAcquisition" class="secondary-button">Aplicar</button>
      </div>

      <div v-if="loadingAcquisition" class="flex py-6">
        <loader-icon class="h-8 w-8 text-primary-500 mx-auto"></loader-icon>
      </div>

      <div
        v-else-if="acquisition"
        class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
      >
        <div class="bg-white rounded-lg shadow p-4 border-l-4 border-primary-500">
          <p class="text-sm text-gray-500 mb-1">Usuarios nuevos</p>
          <p class="text-2xl font-bold text-primary-500">{{ acquisition.new_users }}</p>
        </div>
        <div class="bg-white rounded-lg shadow p-4">
          <p class="text-sm text-gray-500 mb-1">Clientes con compras</p>
          <p class="text-2xl font-bold text-gray-700">{{ acquisition.clients_with_purchases }}</p>
        </div>
        <div class="bg-white rounded-lg shadow p-4">
          <p class="text-sm text-gray-500 mb-1">Mascotas nuevas</p>
          <p class="text-2xl font-bold text-gray-700">{{ acquisition.new_pets }}</p>
        </div>
        <div class="bg-white rounded-lg shadow p-4 border-l-4 border-success-500">
          <p class="text-sm text-gray-500 mb-1">Planes nuevos activados</p>
          <p class="text-2xl font-bold text-success-500">{{ acquisition.new_plans }}</p>
        </div>
        <div class="bg-white rounded-lg shadow p-4">
          <p class="text-sm text-gray-500 mb-1">Recompras (planes)</p>
          <p class="text-2xl font-bold text-gray-700">{{ acquisition.renewed_plans }}</p>
        </div>
      </div>
    </div>

    <div v-if="!loading">
      <!-- Year selector -->
      <div class="flex flex-wrap items-center gap-2 mb-2">
        <button
          v-for="(year, index) in availableYears"
          :key="index"
          :class="[
            'px-3 py-1 rounded-full text-sm font-medium transition-colors',
            selectedYear === year
              ? 'bg-primary-500 text-white shadow'
              : 'bg-white text-content-300 shadow hover:text-primary-500',
          ]"
          @click="selectYear(year)"
        >
          {{ year }}
        </button>
      </div>

      <!-- Month selector -->
      <div class="flex flex-wrap items-center gap-2 mb-6">
        <button
          v-for="(month, index) in availableMonths"
          :key="index"
          :class="[
            'px-3 py-1 rounded-full text-sm font-medium transition-colors',
            selectedMonth === month.number
              ? 'bg-primary-500 text-white shadow'
              : 'bg-white text-content-300 shadow hover:text-primary-500',
          ]"
          @click="selectMonth(month)"
        >
          {{ month.label }}
        </button>
      </div>

      <!-- Empty state when no month selected -->
      <div v-if="!report && selectedMonth === null" class="card text-center py-10">
        <p class="text-content-300">Selecciona un mes para ver el reporte.</p>
      </div>

      <div v-if="report">
        <!-- General stats grid -->
        <h2 class="text-lg font-semibold text-gray-700 mb-3">Salud de Clientes — General</h2>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          <div class="bg-white rounded-lg shadow p-4 border-l-4 border-success-500">
            <p class="text-sm text-gray-500 mb-1">Planes activos</p>
            <p class="text-2xl font-bold text-success-500">{{ report.active_quotes }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Planes suspendidos</p>
            <p class="text-2xl font-bold text-warning-500">{{ report.suspended_quotes }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Cancelados (este mes)</p>
            <p class="text-2xl font-bold text-error-500">{{ report.canceled_quotes }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4 border-l-4 border-warning-500">
            <p class="text-sm text-gray-500 mb-1">Tasa de abandono</p>
            <p
              class="text-2xl font-bold"
              :class="parseFloat(report.churn_rate) > 5 ? 'text-error-500' : parseFloat(report.churn_rate) > 2 ? 'text-warning-500' : 'text-success-500'"
            >
              {{ parseFloat(report.churn_rate).toFixed(1) }}%
            </p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Clientes en riesgo</p>
            <p class="text-2xl font-bold text-warning-500">{{ report.at_risk_count }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Valor de vida promedio</p>
            <p class="text-2xl font-bold text-gray-800">{{ integerToCurrency(report.avg_lifetime_value) }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Clientes nuevos</p>
            <p class="text-2xl font-bold text-success-500">{{ report.new_clients_count }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Ingresos clientes nuevos</p>
            <p class="text-2xl font-bold text-gray-800">{{ integerToCurrency(report.new_client_revenue) }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Ingresos recurrentes</p>
            <p class="text-2xl font-bold text-gray-800">{{ integerToCurrency(report.returning_client_revenue) }}</p>
          </div>
        </div>

        <!-- Suspension and cancellation reasons -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <!-- Suspension reasons -->
          <div class="bg-white rounded-lg shadow p-4">
            <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Razones de suspensión</h3>
            <div v-if="report.suspension_reasons && report.suspension_reasons.length > 0">
              <div
                v-for="reason in report.suspension_reasons"
                :key="reason.reason"
                class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <span class="text-sm text-gray-600">{{ reason.reason }}</span>
                <span class="ml-4 px-2 py-0.5 bg-yellow-100 text-warning-500 rounded-full text-xs font-semibold">{{ reason.count }}</span>
              </div>
            </div>
            <p v-else class="text-sm text-content-300">Sin suspensiones registradas</p>
          </div>

          <!-- Cancellation reasons -->
          <div class="bg-white rounded-lg shadow p-4">
            <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Razones de cancelación (este mes)</h3>
            <div v-if="report.cancel_reasons && report.cancel_reasons.length > 0">
              <div
                v-for="reason in report.cancel_reasons"
                :key="reason.reason"
                class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
              >
                <span class="text-sm text-gray-600">{{ reason.reason }}</span>
                <span class="ml-4 px-2 py-0.5 bg-red-100 text-error-500 rounded-full text-xs font-semibold">{{ reason.count }}</span>
              </div>
            </div>
            <p v-else class="text-sm text-content-300">Sin cancelaciones registradas</p>
          </div>
        </div>

        <!-- Franchises section -->
        <h2 class="text-lg font-semibold text-gray-700 mb-3">Franquicias</h2>

        <div
          v-if="!report.franchises || report.franchises.length === 0"
          class="card text-center py-6"
        >
          <p class="text-content-300">Sin datos para este período</p>
        </div>

        <div class="flex flex-col gap-4">
          <div
            v-for="franchise in report.franchises"
            :key="franchise.franchise_id"
            class="bg-white rounded-lg shadow overflow-hidden"
          >
            <!-- Franchise header -->
            <div
              class="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
              @click="toggleFranchise(franchise.franchise_id)"
            >
              <div class="flex items-center gap-3">
                <div class="w-2 h-8 rounded-full bg-primary-500"></div>
                <div>
                  <h3 class="font-semibold text-gray-800">{{ franchise.name }}</h3>
                  <p class="text-xs text-gray-500">{{ franchise.branches ? franchise.branches.length : 0 }} sucursal(es)</p>
                </div>
              </div>
              <div class="flex items-center gap-4">
                <div class="text-right hidden sm:block">
                  <p class="text-xs text-gray-500">Activos</p>
                  <p class="font-semibold text-success-500">{{ franchise.active_quotes }}</p>
                </div>
                <div class="text-right hidden sm:block">
                  <p class="text-xs text-gray-500">Suspendidos</p>
                  <p class="font-semibold text-warning-500">{{ franchise.suspended_quotes }}</p>
                </div>
                <div class="text-right hidden sm:block">
                  <p class="text-xs text-gray-500">Cancelados</p>
                  <p class="font-semibold text-error-500">{{ franchise.canceled_quotes }}</p>
                </div>
                <div class="text-right hidden md:block">
                  <p class="text-xs text-gray-500">Abandono</p>
                  <p
                    class="font-semibold"
                    :class="parseFloat(franchise.churn_rate) > 5 ? 'text-error-500' : parseFloat(franchise.churn_rate) > 2 ? 'text-warning-500' : 'text-success-500'"
                  >
                    {{ parseFloat(franchise.churn_rate).toFixed(1) }}%
                  </p>
                </div>
                <div class="text-right hidden md:block">
                  <p class="text-xs text-gray-500">En riesgo</p>
                  <p class="font-semibold text-warning-500">{{ franchise.at_risk_count }}</p>
                </div>
                <svg
                  :class="['w-5 h-5 text-gray-400 transition-transform', isFranchiseExpanded(franchise.franchise_id) ? 'rotate-180' : '']"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <!-- Expanded branch table -->
            <div v-if="isFranchiseExpanded(franchise.franchise_id)" class="border-t border-gray-100">
              <!-- Stats row for mobile -->
              <div class="flex flex-wrap gap-4 px-5 py-3 bg-gray-50 sm:hidden text-sm">
                <div><span class="text-gray-500">Activos:</span> <span class="font-semibold text-success-500">{{ franchise.active_quotes }}</span></div>
                <div><span class="text-gray-500">Suspendidos:</span> <span class="font-semibold text-warning-500">{{ franchise.suspended_quotes }}</span></div>
                <div><span class="text-gray-500">Cancelados:</span> <span class="font-semibold text-error-500">{{ franchise.canceled_quotes }}</span></div>
                <div><span class="text-gray-500">Abandono:</span> <span class="font-semibold">{{ parseFloat(franchise.churn_rate).toFixed(1) }}%</span></div>
                <div><span class="text-gray-500">En riesgo:</span> <span class="font-semibold text-warning-500">{{ franchise.at_risk_count }}</span></div>
              </div>
              <div class="overflow-x-auto">
                <table class="min-w-full text-sm">
                  <thead class="bg-primary-100">
                    <tr>
                      <th class="px-5 py-2 text-left text-xs font-semibold text-primary-700 uppercase tracking-wide">Sucursal</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Activos</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Suspendidos</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Cancelados</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Abandono</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">En riesgo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(branch, bIndex) in franchise.branches"
                      :key="branch.branch_id"
                      :class="bIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                    >
                      <td class="px-5 py-2 font-medium text-gray-800">{{ branch.branch_name }}</td>
                      <td class="px-4 py-2 text-right text-success-500 font-medium">{{ branch.active_quotes }}</td>
                      <td class="px-4 py-2 text-right text-warning-500 font-medium">{{ branch.suspended_quotes }}</td>
                      <td class="px-4 py-2 text-right text-error-500 font-medium">{{ branch.canceled_quotes }}</td>
                      <td
                        class="px-4 py-2 text-right font-semibold"
                        :class="parseFloat(branch.churn_rate) > 5 ? 'text-error-500' : parseFloat(branch.churn_rate) > 2 ? 'text-warning-500' : 'text-success-500'"
                      >
                        {{ parseFloat(branch.churn_rate).toFixed(1) }}%
                      </td>
                      <td class="px-4 py-2 text-right text-warning-500 font-medium">{{ branch.at_risk_count }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "CustomerHealth",

  data() {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    return {
      report: null,
      loading: false,
      acquisition: null,
      loadingAcquisition: false,
      acquisitionFrom: fmt(firstOfMonth),
      acquisitionTo: fmt(now),
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      expandedFranchises: [],
      months: [
        { number: 1, label: "Enero" },
        { number: 2, label: "Febrero" },
        { number: 3, label: "Marzo" },
        { number: 4, label: "Abril" },
        { number: 5, label: "Mayo" },
        { number: 6, label: "Junio" },
        { number: 7, label: "Julio" },
        { number: 8, label: "Agosto" },
        { number: 9, label: "Septiembre" },
        { number: 10, label: "Octubre" },
        { number: 11, label: "Noviembre" },
        { number: 12, label: "Diciembre" },
      ],
    };
  },

  computed: {
    availableYears() {
      const currentYear = new Date().getFullYear();
      const startingYear = 2020;
      const years = [];

      for (let year = startingYear; year <= currentYear; year++) {
        years.push(year);
      }

      return years;
    },

    availableMonths() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      if (this.selectedYear === currentYear) {
        return this.months.filter((month) => month.number <= currentMonth);
      }

      return this.months;
    },
  },

  created() {
    this.loadReport();
    this.loadAcquisition();
  },

  methods: {
    integerToCurrency,

    async loadAcquisition() {
      this.loadingAcquisition = true;

      try {
        this.acquisition = await api.reports.getCustomerAcquisitionReport([
          { name: "from", value: this.acquisitionFrom },
          { name: "to", value: this.acquisitionTo },
        ]);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingAcquisition = false;
    },

    toggleFranchise(id) {
      const index = this.expandedFranchises.indexOf(id);
      if (index === -1) {
        this.expandedFranchises.push(id);
      } else {
        this.expandedFranchises.splice(index, 1);
      }
    },

    isFranchiseExpanded(id) {
      return this.expandedFranchises.includes(id);
    },

    async loadReport() {
      this.loading = true;

      try {
        this.report = await api.reports.getCustomerHealthReport([
          { name: "year", value: this.selectedYear },
          {
            name: "month",
            value: this.selectedMonth,
          },
        ]);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    selectYear(year) {
      if (year === this.selectedYear) {
        return;
      }

      this.selectedYear = year;
      this.selectedMonth = null;
      this.report = null;
    },

    selectMonth(month) {
      if (this.selectedMonth === month) {
        return;
      }

      this.selectedMonth = month.number;

      this.loadReport();
    },
  },
};
</script>
